import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { AlertActions } from '../../common/react/AlertActions';
import { DeprecatedReactMenu } from '../../common/react/components/Menu';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { Telemetry } from '../../ts/services/TelemetryService';
import { PageHeaderButtonType, PageHeaderIconPaths } from '../layout/PageHeader';
import { SessionServerSelectors } from '../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../selectors/SiteUrlsSelectors';
import { AppState } from '../state/AppState';
import { MapDispatchToProps, MapStateToProps } from '../utils/ConnectUtils';
import { AlertsMenuPanel, AlertsMenuPanelProps } from './AlertsMenuPanel';
import { HeaderMenuButton } from './HeaderMenuButton';
import { HeaderStyles } from './HeaderStyles';

export interface AlertsMenuOwnProps {
  readonly style?: any;
  readonly buttonType: PageHeaderButtonType;
  readonly telemetryService: Telemetry;
}

export interface AlertsMenuPropsFromReduxDispatch {
  readonly alertActions: AlertActions;
}

export interface AlertsMenuPropsFromReduxState {
  readonly alertCount?: number;
  readonly alertFailureCountThreshold: number;
  readonly alertLookBackDaysLimit: number;
  readonly alertFailureCountEnabled: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
  readonly siteUrls: SiteUrls;
}

export type AlertsMenuViewComponentProps = AlertsMenuOwnProps & AlertsMenuPropsFromReduxState & AlertsMenuPropsFromReduxDispatch;

interface AlertIcons {
  readonly noAlerts: PageHeaderIconPaths;
  readonly withAlerts: PageHeaderIconPaths;
}

const RegularAlertIcons: AlertIcons = {
  noAlerts:
  {
    inactive: imgPath('spaces_toolbar_alert.svg'),
    active: imgPath('spaces_toolbar_alert_active.svg')
  },
  withAlerts: {
    inactive: imgPath('spaces_toolbar_alert_notification_green.svg'),
    active: imgPath('spaces_toolbar_alert_notification_green_active.svg')
  }
};

const LightAlertIcons: AlertIcons = {
  noAlerts:
  {
    inactive: imgPath('spaces_toolbar_alert_white.svg'),
    active: imgPath('spaces_toolbar_alert_active_white.svg')
  },
  withAlerts: {
    inactive: imgPath('spaces_toolbar_alert_notification_green_white.svg'),
    active: imgPath('spaces_toolbar_alert_notification_green_active_white.svg')
  }
};

export class AlertsMenuViewComponent extends React.PureComponent<AlertsMenuViewComponentProps> {

  public componentDidMount() {
    this.props.alertActions.getAlertCount();
  }

  private itemSelected = () => {
    const ref = (<DeprecatedReactMenu>this.refs['menu']);
    if (ref) {
      ref.closeMenu();
    }
    window.removeEventListener('blur', this.onWindowBlur);
  }

  public render() {

    return DeprecatedReactMenu.element({
      ref: 'menu',
      tbTestId: 'alerts-menu',
      style: this.props.style,
      alignPanelRight: true,

      menuButtonFactory: (props?: HeaderMenuButton.Props) => {

        const alertCount = this.props.alertCount;

        props.iconPath = this.getAlertsIcon(alertCount, props, this.props.buttonType);

        props.label = _.isNumber(alertCount) && alertCount > 0 ? alertCount.toString() : '';
        props.style = {
          fontSize: HeaderStyles.AlertsCountFontSize
        };

        props.tabIndex = 0;
        props.ariaExpanded = props.menuOpen;
        props.ariaLabel = Messages.alertsAltText();
        props.altText = Messages.alertsAltText();
        props.title = (_.isNumber(alertCount) && alertCount > 0) ? Messages.alertsTitle() : Messages.alertsNoneTitle();

        return HeaderMenuButton.element(props);
      },

      menuPanelFactory: (props?: AlertsMenuPanelProps) => {
        props.itemSelected = this.itemSelected;
        props.alertActions = this.props.alertActions;
        props.siteUrls = this.props.siteUrls;
        props.alertFailureCountThreshold = this.props.alertFailureCountThreshold;
        props.alertLookBackDaysLimit = this.props.alertLookBackDaysLimit;
        props.alertFailureCountEnabled = this.props.alertFailureCountEnabled;
        props.flowFailureThresholdForRunPrevention = this.props.flowFailureThresholdForRunPrevention;
        return AlertsMenuPanel.element(props);
      },
      onBeforeMenuOpen: () => {
        this.props.telemetryService.sendEvent<PageHeaderButtonClickEvent>({
          type: TelemetryEvents.PageHeaderButtonClick,
          button: 'alert-menu',
          action: 'open'
        });
        window.addEventListener('blur', this.onWindowBlur);
        return Q.when();
      },
    });
  }

  private onWindowBlur = () => {
    this.itemSelected();
  }

  private getAlertsIcon(alertCount: number, props: HeaderMenuButton.Props, buttonType: PageHeaderButtonType): string {
    const alertIcons: AlertIcons = (buttonType === 'light') ? LightAlertIcons : RegularAlertIcons;

    if (alertCount > 0) {
      return (props.hovered || props.menuOpen) ? alertIcons.withAlerts.active : alertIcons.withAlerts.inactive;
    } else {
      return (props.hovered || props.menuOpen) ? alertIcons.noAlerts.active : alertIcons.noAlerts.inactive;
    }
  }
}

export namespace ReduxConnectedAlertsMenu {

  const mapStateToProps: MapStateToProps<AlertsMenuPropsFromReduxState> = (state: AppState) => ({
    alertCount: state.alertListInfo.total,
    alertFailureCountEnabled: SessionServerSelectors.alertFailureCountThreshold(state) > 0,
    alertFailureCountThreshold: SessionServerSelectors.alertFailureCountThreshold(state),
    alertLookBackDaysLimit: SessionServerSelectors.alertLookBackDaysLimit(state),
    flowFailureThresholdForRunPrevention: SessionServerSelectors.flowFailureThresholdForRunPrevention(state),
    siteUrls: siteUrlsSelector(state),
  });

  const mapDispatchToProps: MapDispatchToProps<AlertsMenuPropsFromReduxDispatch> = (dispatch: Dispatch<AppState>) => ({
    alertActions: memoizedMakeAlertActions(dispatch),
  });

  const memoizedMakeAlertActions = defaultMemoize((dispatch: Dispatch<AppState>) => new AlertActions(dispatch));

  export const HOC = connect(mapStateToProps, mapDispatchToProps);
}


export const AlertsMenu: React.ComponentType<AlertsMenuOwnProps> = ReduxConnectedAlertsMenu.HOC(AlertsMenuViewComponent);


